import React, { useState, useEffect } from 'react';
import { contactService } from '../../../api';
import Sidebar from '../sidebar/Sidebar';
import { FaBell, FaEnvelope } from 'react-icons/fa';
import './ContactList.css';

const ContactList = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchContacts();
    // Vérifier les nouvelles messages toutes les 30 secondes
    const interval = setInterval(fetchContacts, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Calculer le nombre de messages non lus
    const count = contacts.filter(contact => !contact.read_at).length;
    setUnreadCount(count);
    
    // Mettre à jour le titre de l'onglet
    document.title = count > 0 
      ? `(${count}) Messages - Admin` 
      : 'Messages - Admin';
  }, [contacts]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const data = await contactService.getAll();
      setContacts(data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des contacts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await contactService.markAsRead(id);
      const updatedContacts = contacts.map(contact => 
        contact.id === id ? { ...contact, read_at: new Date().toISOString() } : contact
      );
      setContacts(updatedContacts);
      localStorage.setItem(`contact_${id}_read`, 'true');
    } catch (err) {
      setError('Erreur lors du marquage comme lu');
      console.error(err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await contactService.markAllAsRead();
      const updatedContacts = contacts.map(contact => 
        ({ ...contact, read_at: contact.read_at || new Date().toISOString() })
      );
      setContacts(updatedContacts);
      // Marquer tous comme lus dans le localStorage
      contacts.forEach(contact => {
        localStorage.setItem(`contact_${contact.id}_read`, 'true');
      });
    } catch (err) {
      setError('Erreur lors du marquage de tous comme lus');
      console.error(err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non lu';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const filteredContacts = contacts.filter(contact => {
    if (filter === 'read') return contact.read_at;
    if (filter === 'unread') return !contact.read_at;
    return true;
  });

  useEffect(() => {
    const updatedContacts = contacts.map(contact => {
      const isRead = localStorage.getItem(`contact_${contact.id}_read`) === 'true';
      return isRead && !contact.read_at 
        ? { ...contact, read_at: new Date().toISOString() } 
        : contact;
    });
    setContacts(updatedContacts);
  }, [contacts.length]);

  return (
    <div className="admin-container">
      <Sidebar />
      
      <div className="contact-content">
        <div className="contact-header">
          <div className="header-title">
            <FaEnvelope className="header-icon" />
            <h2>Liste des Contacts</h2>
            {unreadCount > 0 && (
              <div className="notification-badge">
                <FaBell className="bell-icon" />
                <span className="badge-count">{unreadCount}</span>
              </div>
            )}
          </div>
          
          <div className="header-actions">
            <div className="filter-buttons">
              <button 
                onClick={() => setFilter('all')}
                className={filter === 'all' ? 'active' : ''}
              >
                Tous
              </button>
              <button 
                onClick={() => setFilter('unread')}
                className={filter === 'unread' ? 'active' : ''}
              >
                Non lus
              </button>
              <button 
                onClick={() => setFilter('read')}
                className={filter === 'read' ? 'active' : ''}
              >
                Lus
              </button>
            </div>
            
            {unreadCount > 0 && (
              <button 
                onClick={handleMarkAllAsRead}
                className="mark-all-read-btn"
              >
                Tout marquer comme lu
              </button>
            )}
          </div>
        </div>
        
        {loading && <div className="loading">Chargement en cours...</div>}
        {error && <div className="error">{error}</div>}

        <table className="contact-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Sujet</th>
              <th>Message</th>
              <th>Date</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredContacts.length > 0 ? (
              filteredContacts.map(contact => (
                <tr key={contact.id} className={!contact.read_at ? 'unread' : ''}>
                  <td>{contact.name}</td>
                  <td>{contact.email}</td>
                  <td>{contact.phone || '-'}</td>
                  <td>{contact.subject}</td>
                  <td className="message-cell">{contact.message}</td>
                  <td>{formatDate(contact.created_at)}</td>
                  <td>
                    {contact.read_at 
                      ? <span className="status-read">Lu</span> 
                      : <span className="status-unread">Non lu</span>
                    }
                  </td>
                  <td>
                    {!contact.read_at && (
                      <button 
                        onClick={() => handleMarkAsRead(contact.id)}
                        className="mark-read-btn"
                      >
                        Marquer comme lu
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-results">
                  Aucun contact trouvé
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContactList;